/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { useRef, useState } from 'react';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import { Headline, Icon, Layout, TextLink } from '@oneaudi/unified-web-components';
import { FootnoteContextProvider, useInViewEffect } from '@oneaudi/feature-app-utils';
import type { VueFormatterServiceInterfaceV1 } from '../feature-service-types';
import { renderTextWithFootnotesReferencesV2 } from '../feature-service-types';
import { UeElement } from '@oneaudi/falcon-tools';
import type { EditorialTextProps } from '../../../@types/types';
import { processBodyText } from './processBodyText';
import { getValidCtaAriaLabel } from '../../utils/ariaLabelHelper';
import { getThemeName } from '../../utils/alphaHelper';
import type { ThemeName } from '@oneaudi/unified-web-alpha-common-v2/dist/cjs/components/theme-provider/theme';
import {
  ContainerDiv,
  ButtonContainerDiv,
  TextWrapperDiv,
  TextLayout,
  StyledTextLayoutItem,
} from './styledComponents';
import { ConsumptionAndEmission } from './ConsumptionAndEmission';
import Paragraphs from './Paragraphs';
import { useEditorialAnchors } from '../../hooks/useEditorialAnchors';
import SitewideSearch from './SitewideSearch';
import { IconWrapper } from './SitewideSearch/SidewideSearch.styles';

export const EditorialText = ({
  content,
  footnoteReferenceService,
  vueFormatterService,
  localeService,
  trackingService,
  dealerContextService,
  logger,
  showSearch,
}: EditorialTextProps) => {
  const eTextRef = useRef<HTMLDivElement | null>(null);
  const eTextContent = useRef<HTMLDivElement | null>(null);
  const {
    bodyText,
    theme,
    justification,
    ctaButtonLabel,
    ctaButtonUrl,
    ctaButtonOpenInNewTab,
    ariaLabel,
    consumptionAndEmission,
    disclaimer,
    bottomMargin,
    reduceTextWidth,
    paragraphs,
    headline,
    headlineOrder,
    headlineTag,
    sortBy,
    showPrice,
  } = content;

  const [sitewideSearchOpen, setSidewideSearchOpen] = useState(false);

  const themeActive = getThemeName((theme || 'light') as ThemeName);
  const processedCopy = bodyText ? processBodyText(bodyText as string) : undefined;
  const hasCta = Boolean(ctaButtonUrl?.length && ctaButtonLabel?.length);

  const getFirstElementInnerText = React.useCallback((): string => {
    const getFirstLineFromBodyText = (text: string): string => {
      try {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = text;
        const cleanText = tempDiv.textContent || tempDiv.innerText || '';
        const firstLine = cleanText.split(/\r?\n|\r/).find((line) => line.trim().length > 0);
        return firstLine ? firstLine.trim() : '';
      } catch {
        return '';
      }
    };

    const fallback = () => getFirstLineFromBodyText((bodyText as string) || '');

    try {
      if (!eTextContent.current?.firstChild) return fallback();

      let firstText = '';

      const titleElements = eTextContent.current.querySelectorAll('h1, h2, h3, h4, h5, h6');
      if (titleElements.length > 0) {
        const firstTitle = titleElements[0] as HTMLElement;
        firstText = firstTitle.innerText || firstTitle.textContent || '';
        if (firstText.trim()) return firstText.trim();
      }

      const firstChild = eTextContent.current.firstChild as HTMLElement;
      if (firstChild?.tagName && firstChild.tagName !== 'DIV') {
        firstText = firstChild.innerText || '';
        if (firstText.trim()) return firstText.trim();
      }

      const level3 = eTextContent.current.firstChild?.firstChild?.firstChild;
      if (!level3) return fallback();

      const nestedElement = level3 as HTMLElement;
      if (nestedElement.innerHTML !== undefined) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = nestedElement.innerHTML;
        const cleanText = tempDiv.textContent || tempDiv.innerText || '';
        if (cleanText.trim()) return cleanText.trim();
      }

      return fallback();
    } catch {
      return fallback();
    }
  }, [bodyText]);

  useInViewEffect(() => {
    const readyEvent = {
      event: {
        attributes: {
          componentName: 'editorial-text',
          elementName: '',
          label: '',
          clickID: '',
          pos: '',
          targetURL: '',
          value: getFirstElementInnerText(),
        },
        eventInfo: {
          eventAction: 'impression',
          eventName: `editorial text - impression`,
        },
      },
    };
    trackingService?.track(readyEvent);
  });

  const addTrackingClickEventCallback = React.useCallback(
    (cta: HTMLAnchorElement) => {
      const getUrlExtension = (url: string) => url?.split(/[#?]/)[0].split('.').pop()?.trim();

      const setEventAction = (ctaUrl: string) => {
        const isExternalURL = (url: string) => new URL(url).origin !== window.location.origin;
        if (getUrlExtension(ctaUrl) === 'pdf' || getUrlExtension(ctaUrl) === 'xlsx')
          return 'download';
        if (ctaUrl === '') return 'content';
        if (isExternalURL(ctaUrl)) return 'exit_link';
        return 'internal_link';
      };

      const isFullUrl = (url: string) => {
        if (url.match(/(www|http:|https:)+[^\s]+[\w]/gi)) return url;
        return new URL(url, window.location.href).href;
      };

      const url = cta.href;
      const eventAction = setEventAction(url);
      const extension = getUrlExtension(url);
      const dataLayerEvent = {
        eventInfo: {
          eventAction,
          eventName: `editorial text - click ${eventAction === 'download' ? 'to download' : 'on text link'}`,
        },
        attributes: {
          componentName: 'editorial-text',
          label: cta.innerText,
          targetURL: isFullUrl(url),
          clickID: '',
          elementName: 'text link',
          value: cta.parentElement?.previousElementSibling?.innerHTML
            ? cta.parentElement?.previousElementSibling?.innerHTML
            : getFirstElementInnerText(),
          pos: '',
          relatedElements:
            extension === 'pdf' || extension === 'xlsx'
              ? [
                  {
                    elementName: `${extension} file`,
                    elementValue: url
                      .split('/')
                      .pop()
                      ?.replace(/.pdf|.xlsx/gi, ''),
                  },
                ]
              : [{ elementValue: cta.innerHTML }],
        },
      };

      cta.onclick = () => {
        trackingService?.track({ event: dataLayerEvent });
      };
    },
    [trackingService, getFirstElementInnerText],
  );

  useEditorialAnchors(eTextRef, addTrackingClickEventCallback);

  return (
    <div data-theme-name={themeActive}>
      <FootnoteContextProvider footnoteReferenceService={footnoteReferenceService}>
        <ThemeProvider theme={{ name: themeActive }}>
          <ContainerDiv
            justification={justification}
            bottomMargin={bottomMargin}
            reduceTextWidth={reduceTextWidth}
            ref={eTextRef}
            data-testid="container"
            hasBodyText={Boolean(bodyText)}
          >
            <TextLayout
              reduceTextWidth={reduceTextWidth}
              theme={themeActive}
              data-testid="text-layout"
            >
              <Layout data-test-id="title" direction="column">
                <StyledTextLayoutItem basis="100%">
                  <div>
                    {headline && (
                      <Headline
                        variant={{ type: 'headline', order: headlineOrder, weight: 'normal' }}
                        tag={headlineTag}
                        className="edirotial-text-headline"
                        data-testid="main-headline"
                      >
                        {renderTextWithFootnotesReferencesV2(headline as string)}
                      </Headline>
                    )}
                    {bodyText && (
                      <TextWrapperDiv
                        data-testid="text-wrapper"
                        align={justification}
                        theme={themeActive}
                        ref={eTextContent}
                      >
                        {processedCopy}
                      </TextWrapperDiv>
                    )}
                  </div>
                </StyledTextLayoutItem>
                {hasCta && (
                  <StyledTextLayoutItem basis="auto">
                    <ButtonContainerDiv align={justification} data-testid="button-container">
                      <TextLink
                        variant="standalone"
                        size="md"
                        target={ctaButtonOpenInNewTab === 'newTab' ? '_blank' : '_self'}
                        href={ctaButtonUrl}
                        onClick={() => {}}
                        icon="forward"
                        iconPosition="trailing"
                        aria-label={getValidCtaAriaLabel(
                          ariaLabel as string,
                          ctaButtonLabel as string,
                        )}
                        data-testid="main-cta"
                      >
                        <UeElement type="text" property="ctaButtonLabel" label="CTA Label">
                          {ctaButtonLabel}
                        </UeElement>
                      </TextLink>
                    </ButtonContainerDiv>
                  </StyledTextLayoutItem>
                )}

                <TextLayout reduceTextWidth={reduceTextWidth} theme={themeActive}>
                  <Layout data-test-id="title-paragraph" direction="column">
                    <StyledTextLayoutItem basis="100%">
                      {paragraphs && paragraphs.length > 0 && (
                        <Paragraphs
                          eTextContent={eTextContent}
                          paragraphs={paragraphs}
                          justification={justification}
                          themeActive={themeActive}
                        />
                      )}
                    </StyledTextLayoutItem>
                  </Layout>
                </TextLayout>
                {consumptionAndEmission?.length > 0 || disclaimer?.length > 0 ? (
                  <ConsumptionAndEmission
                    consumptionAndEmission={consumptionAndEmission}
                    disclaimer={disclaimer}
                    theme={themeActive}
                    vueFormatterService={vueFormatterService as VueFormatterServiceInterfaceV1}
                    align={justification}
                    localeService={localeService!}
                    logger={logger}
                  />
                ) : null}
              </Layout>
            </TextLayout>
            {showSearch && (
              <IconWrapper
                onClick={() => {
                  setSidewideSearchOpen(true);
                }}
                data-testid="sitewide-open-icon"
                aria-label="sitewide-open-icon"
              >
                <Icon name="search" size="s" />
              </IconWrapper>
            )}
            {sitewideSearchOpen && (
              <SitewideSearch
                setSidewideSearchOpen={setSidewideSearchOpen}
                localeService={localeService}
                dealerContextService={dealerContextService}
                sortBy={sortBy}
                showPrice={showPrice}
              />
            )}
          </ContainerDiv>
        </ThemeProvider>
      </FootnoteContextProvider>
    </div>
  );
};
